import { Button, NativeSelect, TextInput } from '@mantine/core';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { FormSummary } from '@/api';
import {
  byLatestMessage,
  FormNameCell,
  LastMessageCell,
  ProblemsCell,
  protectionText,
  RecipientsCell,
} from '@/components/FormsList/cells';
import { CopyButton } from '@/components/Ui/CopyButton';
import { DataTable } from '@/components/Ui/DataTable';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { submissionUrl } from '@/lib/form-site';
import { formatNumber } from '@/lib/money';
import { parseTimestamp } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import classes from './List.module.css';

const PAGE_SIZE = 25;

type Show = 'all' | 'problems' | 'no_recipients';
type Sort = 'latest' | 'most' | 'name' | 'newest';

const sorters: Record<Sort, (a: FormSummary, b: FormSummary) => number> = {
  latest: byLatestMessage,
  most: (a, b) => b.messages_7d - a.messages_7d || byLatestMessage(a, b),
  name: (a, b) => a.title.localeCompare(b.title),
  newest: (a, b) => parseTimestamp(b.created_at).getTime() - parseTimestamp(a.created_at).getTime(),
};

const hasProblems = (f: FormSummary) => f.problems_action > 0;
const noRecipients = (f: FormSummary) => f.recipients_verified === 0;

export function FormsListScreen() {
  const { t, i18n } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const forms = useRustyState((s) => s.formsSummary);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<Show>('all');
  const [sort, setSort] = useState<Sort>('latest');
  const [page, setPage] = useState(0);
  const searchId = useId();
  const showId = useId();
  const sortId = useId();

  useEffect(() => {
    void useRustyState
      .getState()
      .refreshFormsSummary()
      .then((res) => setFailed(res === null));
  }, []);

  const all = forms ?? [];
  const q = query.trim().toLowerCase();
  const filtered = all
    .filter((f) => !q || f.title.toLowerCase().includes(q) || f.id.toLowerCase().includes(q))
    .filter((f) =>
      show === 'problems' ? hasProblems(f) : show === 'no_recipients' ? noRecipients(f) : true
    )
    .sort(sorters[sort]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const rows = filtered.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  const resetPage =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(0);
    };

  return (
    <Page width="wide">
      <PageHeader
        documentTitle={t('forms.pageTitle')}
        title={
          <span className={classes.title}>
            {t('forms.pageTitle')}
            {forms && (
              <span className={classes.count}>{formatNumber(forms.length, i18n.language)}</span>
            )}
          </span>
        }
        actions={
          <Button component={Link} to={createLanguageURL('/account/forms/new')} variant="filled">
            {t('forms.list.newForm')}
          </Button>
        }
      />

      <div className={classes.toolbar}>
        <TextInput
          id={searchId}
          type="search"
          aria-label={t('forms.list.searchLabel')}
          placeholder={t('forms.list.searchPlaceholder')}
          value={query}
          onChange={(e) => resetPage(setQuery)(e.currentTarget.value)}
          className={classes.search}
        />
        <label htmlFor={showId} className={classes.label}>
          {t('forms.list.show')}
        </label>
        <NativeSelect
          id={showId}
          value={show}
          onChange={(e) => resetPage(setShow)(e.currentTarget.value as Show)}
          data={[
            { value: 'all', label: t('forms.list.showAll') },
            {
              value: 'problems',
              label: t('forms.list.showProblems', { count: all.filter(hasProblems).length }),
            },
            {
              value: 'no_recipients',
              label: t('forms.list.showNoRecipients', { count: all.filter(noRecipients).length }),
            },
          ]}
        />
        <label htmlFor={sortId} className={classes.label}>
          {t('forms.list.sort')}
        </label>
        <NativeSelect
          id={sortId}
          value={sort}
          onChange={(e) => resetPage(setSort)(e.currentTarget.value as Sort)}
          data={[
            { value: 'latest', label: t('forms.list.sortLatest') },
            { value: 'most', label: t('forms.list.sortMost') },
            { value: 'name', label: t('forms.list.sortName') },
            { value: 'newest', label: t('forms.list.sortNewest') },
          ]}
        />
      </div>

      {failed && !forms ? (
        <EmptyText>{t('forms.list.loadFailed')}</EmptyText>
      ) : (
        <div className={classes.table}>
          <DataTable
            caption={t('forms.pageTitle')}
            rowKey={(f) => f.id}
            rows={rows}
            empty={
              !forms
                ? t('ui.loading')
                : all.length === 0
                  ? t('forms.list.empty')
                  : t('forms.list.noMatch')
            }
            columns={[
              {
                key: 'form',
                header: t('forms.list.colForm'),
                render: (f) => <FormNameCell form={f} />,
              },
              {
                key: 'n7',
                header: t('forms.list.colLast7'),
                align: 'num',
                render: (f) => formatNumber(f.messages_7d, i18n.language),
              },
              {
                key: 'last',
                header: t('forms.list.colLastMessage'),
                render: (f) => <LastMessageCell form={f} />,
              },
              {
                key: 'recipients',
                header: t('forms.list.colRecipients'),
                render: (f) => <RecipientsCell form={f} />,
              },
              {
                key: 'protection',
                header: t('forms.list.colProtection'),
                render: (f) => protectionText(f, t),
              },
              {
                key: 'url',
                header: t('forms.list.colSubmissionUrl'),
                render: (f) => (
                  <span className={classes.url}>
                    <span className={classes.mono} title={submissionUrl(f.id)}>
                      …/digest/{f.id.slice(0, 8)}…
                    </span>
                    <CopyButton
                      text={submissionUrl(f.id)}
                      ariaLabel={t('forms.list.copyUrl', { name: f.title })}
                    />
                  </span>
                ),
              },
              {
                key: 'problems',
                header: t('forms.list.colProblems'),
                render: (f) => <ProblemsCell form={f} />,
              },
            ]}
          />
        </div>
      )}

      {filtered.length > PAGE_SIZE && (
        <div className={classes.pager}>
          <span className={classes.label}>
            {t('forms.list.range', {
              from: current * PAGE_SIZE + 1,
              to: current * PAGE_SIZE + rows.length,
              total: filtered.length,
            })}
          </span>
          <div className={classes.pagerButtons}>
            <Button size="xs" disabled={current === 0} onClick={() => setPage(current - 1)}>
              {t('forms.list.previous')}
            </Button>
            <Button size="xs" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
              {t('forms.list.next')}
            </Button>
          </div>
        </div>
      )}
    </Page>
  );
}
